/**
 * Visual constants of the zone overlay. Tuned for a phone held 0.7–2.5 m above
 * the water; all distances in metres.
 */
export const RENDER_STYLE = {
  /** Zone fill colour (sRGB hex). */
  zoneColor: '#ff2a2a',
  innerOpacity: 0.25,
  outerOpacity: 0.12,
  /**
   * Distance fade shared by every overlay layer (zone fills, walls, wall base lines, pier
   * lines, finish band, slipway/pier markers): full opacity up to 0.7x settings.viewDistanceM,
   * 0 at viewDistanceM (view-space distance). Driven at runtime via SharedUniforms.uFade;
   * these are only the fallback/default values.
   */
  viewDistanceFadeFrac: 0.7,

  /** Finish line (extended-zone boundary): vertical checkered band. */
  finishOpacity: 0.92,
  /** Angular height of the band (deg) before clamping. */
  finishAngularHeightDeg: 0.6,
  finishMinHeight: 0.8,
  finishMaxHeight: 10,
  /** Long segments are split so the per-vertex band height tracks the distance closely. */
  finishMaxSegment: 10,
  finishWhite: '#ffffff',
  finishBlack: '#101010',

  /**
   * Zone-boundary walls: vertical fences standing on the water along the outer-zone rings,
   * fixed height (NOT distance-scaled, unlike the finish band). Two classes by what's on the
   * other side of the edge: the 150 m line (shared with the inner zone) and the 300 m line
   * (lake.limitLines, the outer zone's lakeward limit).
   */
  wallColor: '#ff2a2a',
  wallHeight: 10.0,
  /** Base alpha at the bottom of the wall (before the top-fade gradient). */
  wallInnerOpacity: 0.6,
  wallOuterOpacity: 0.4,
  /** Vertical opacity gradient: bottom factor 1, top factor wallTopAlpha. */
  wallTopAlpha: 0.25,
  /** Bright top edge strip: height (m) and flat opacity (independent of wallInner/OuterOpacity). */
  wallEdgeHeight: 0.08,
  wallEdgeOpacity: 0.9,
  wallEdgeColor: '#ffffff',

  /**
   * Wall base line: a bright rail lying on the water along every wall segment (LineSegments),
   * so walls read as fences with a rail top (wallEdge*) and bottom.
   */
  wallBaseColor: '#ffffff',
  wallBaseOpacity: 0.9,
  wallBaseY: 0.05,

  /** Pier lines (thin outlines of piers/jetties/notches on the water, not the finish band). */
  pierColor: '#ffffff',
  pierOpacity: 0.8,
  /** Height above the water plane (m), just enough to avoid z-fighting with the fills. */
  pierY: 0.05,

  /**
   * Slipway markers: a thin pole topped by a square OSM-carto sign (leisure=slipway icon on a
   * blue background with a white border, textured from a canvas — see materials.ts
   * createSlipwaySignTexture), coloured with a white pole outline. All horizontal/vertical
   * marker offsets below are fractions of the per-marker angular height `h` (see
   * slipwayAngularHeightDeg), so the pole and the (square) sign keep their proportions and the
   * sign stays billboarded to face the camera at any distance.
   */
  slipwayColor: '#0092da',
  slipwayOutline: '#ffffff',
  osmPierPointColor: '#9e9e9e',
  /** Angular height of the pole (deg) before clamping, like the finish band. */
  slipwayAngularHeightDeg: 2.5,
  slipwayMinHeight: 4,
  slipwayMaxHeight: 40,
  /** Pole half-width, fraction of h (fill and outline). */
  slipwayPoleHalfWidthFrac: 0.02,
  slipwayPoleOutlineHalfWidthFrac: 0.035,
  /** Outline pole top, slightly above the fill pole's top (frac of h) so it reads as a border. */
  slipwayPoleOutlineTopFrac: 1.02,
  /** Sign square: bottom/top (frac of h) and half-side (frac of h, centred on the pole). */
  slipwaySignFracLow: 0.55,
  slipwaySignFracHigh: 1.0,
  slipwaySignHalfSideFrac: 0.225,

  /**
   * Only triangles within this radius of the user are drawn. Default/minimum; ZoneScene raises
   * this per-frame to max(lodRadius, settings.viewDistanceM + 500) so the LOD subset always
   * covers the fade-out distance.
   */
  lodRadius: 3000,
  /** Margin (m) added to settings.viewDistanceM for the LOD culling radius. */
  lodRadiusMargin: 500,
  /** The drawn subset is rebuilt when the user moved this far from the last centre. */
  lodRebuildDistance: 1000,

  /** Camera clip planes. */
  near: 0.1,
  far: 6000,

  /** Sim-mode background. The water disc stays inside the far plane. */
  simSky: '#a9c8de',
  simWater: '#12324d',
  simWaterRadius: 5900,

  /** Debug helpers. */
  horizonRadius: 3000,
  horizonColor: '#ffd400',
  northArrowDistance: 20,
  northArrowLength: 6,
  northArrowWidth: 3,
  northArrowColor: '#00d8ff',

  /** Overlay alpha smoothing time constant (s). */
  overlayFadeTau: 0.2,

  /**
   * Invisible shore occluder: vertical quads along every lakeOutline ring (incl. island
   * holes), colorWrite:false so it only writes depth, hiding zone parts of the lake behind
   * land/peninsulas from the camera. Height is suppressed near the user (GPS error can put
   * them a few metres on the land side of the shore) by shoreOccluderNearM/FarM: 0 height up
   * to NearM, ramping to full height at FarM (horizontal distance from the user).
   */
  shoreOccluderHeight: 25,
  shoreOccluderNearM: 30,
  shoreOccluderFarM: 60,
  /** Ring segments longer than this are subdivided so the near-suppression varies smoothly. */
  shoreOccluderMaxSegment: 20,
} as const;

/** Render order (depth test is off except for the occluder, so this is mostly the paint order). */
export const RENDER_ORDER = {
  /** Must paint before every zone layer: it only writes depth (colorWrite:false), so a zone
   *  fragment behind it must be depth-tested and rejected, not drawn over already-painted color. */
  occluder: 1,
  simWater: -100,
  outer: 10,
  inner: 11,
  pier: 11.5,
  wall: 11.7,
  wallBase: 11.8,
  finish: 12,
  slipway: 13,
  helpers: 20,
} as const;
